/**
 * Merchant — Service Bookings inbox.
 *
 * Lists every booking on the merchant's restaurants with quick-actions:
 *   • Tap the customer mobile  → ``tel:`` link (native dialer)
 *   • Tap the customer address → ``maps:`` URL (Google Maps / Apple Maps)
 *   • "Collect cash" CTA       → opens a modal pre-filled with the
 *     quoted ``amount_total``; merchant can edit the final amount and
 *     close the booking in one tap. A per-booking
 *     ``BookingSettlement`` row is generated server-side at that moment.
 */
import { useEffect, useState } from "react";
import { Phone, MapPin, Loader2, Calendar, IndianRupee, Navigation } from "lucide-react";
import { toast } from "sonner";
import { api, inr } from "../../lib/api";
import CallButton from "../../components/CallButton";
import BookingLocationMap from "../../components/BookingLocationMap";
import { openMap } from "../../lib/openMap";

const PILL = {
    PENDING: "bg-amber-100 text-amber-800",
    CONFIRMED: "bg-emerald-100 text-emerald-800",
    COMPLETED: "bg-blue-100 text-blue-800",
    CANCELLED: "bg-red-100 text-red-700",
};

export default function MerchantBookings() {
    const [rows, setRows] = useState(null);
    const [collect, setCollect] = useState(null);    // { booking }
    const [final, setFinal] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const reload = async () => {
        try { const { data } = await api.get("/merchant/bookings"); setRows(data || []); }
        catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); setRows([]); }
    };
    useEffect(() => { reload(); }, []);

    const setStatus = async (b, status) => {
        try {
            await api.patch(`/merchant/bookings/${b.id}/status`, { status });
            toast.success(status === "CONFIRMED" ? "Confirmed" : "Cancelled");
            reload();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };

    const openCollect = (b) => {
        setCollect({ booking: b });
        setFinal(String(b.amount_total || ""));
    };

    const submitCollect = async () => {
        if (!collect) return;
        const amt = Number(final);
        if (!(amt >= 0)) { toast.error("Enter a valid amount"); return; }
        setSubmitting(true);
        try {
            const { data } = await api.post(`/merchant/bookings/${collect.booking.id}/collect-cash`, { final_amount: amt });
            const net = Number(data.settlement.net_to_merchant || 0);
            toast.success(
                net > 0 ? `Closed — tenant owes you ${inr(net)}` :
                net < 0 ? `Closed — you owe tenant ${inr(Math.abs(net))}` :
                "Closed — fully settled",
            );
            setCollect(null); setFinal("");
            reload();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
        finally { setSubmitting(false); }
    };

    if (rows === null) return <div className="p-8 text-center"><Loader2 className="animate-spin inline" /></div>;
    if (rows.length === 0) {
        return (
            <div className="p-6 text-center text-sm text-muted-foreground" data-testid="mbookings-empty">
                No service bookings yet. They appear here the moment a customer reserves a slot.
            </div>
        );
    }
    return (
        <div className="p-4 space-y-3" data-testid="mbookings-list">
            {rows.map((b) => {
                const addr = renderAddress(b.address);
                // Navigate only makes sense for AT_CUSTOMER bookings — the
                // merchant is the one travelling, to the customer's saved
                // address. AT_SHOP bookings store the SHOP's own address
                // on ``b.address`` (the customer travels here instead), so
                // a "Navigate" button there would just point merchants at
                // their own shop.
                const canNavigate = b.location_type !== "AT_SHOP" && (b.address?.lat != null || addr);
                return (
                    <div key={b.id} data-testid={`mbooking-${b.id}`} className="bg-card border border-border rounded-2xl p-4">
                        <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                                <p className="font-bold line-clamp-1">{b.product_name}</p>
                                <p className="text-xs text-muted-foreground mt-0.5 inline-flex items-center gap-1.5"><Calendar size={11} /> {new Date(b.scheduled_at).toLocaleString()} · {b.duration_min}m</p>
                            </div>
                            <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full ${PILL[b.status]}`}>{b.status}</span>
                        </div>
                        <div className="mt-3 grid grid-cols-1 gap-1 text-sm">
                            <div className="font-semibold">{b.customer_name || "Customer"}</div>
                            {addr && <div className="text-xs text-muted-foreground">{addr}</div>}
                        </div>
                        <BookingLocationMap
                            lat={b.address?.lat}
                            lng={b.address?.lng}
                            label={b.customer_name || b.product_name}
                            className="mt-2"
                            testid={`mbooking-map-${b.id}`}
                        />
                        {/* Quick-action chips: tel:, maps: */}
                        <div className="mt-3 flex flex-wrap gap-2">
                            {b.customer_mobile && (
                                <CallButton
                                    phone={b.customer_mobile}
                                    testid={`mbooking-call-${b.id}`}
                                    className="inline-flex items-center gap-1.5 text-xs font-bold bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400 rounded-full px-3 py-1.5"
                                ><Phone size={13} /> {b.customer_mobile}</CallButton>
                            )}
                            {canNavigate && (
                                <button
                                    type="button"
                                    onClick={() => openMap({
                                        lat: b.address?.lat, lng: b.address?.lng,
                                        label: b.customer_name, address: addr,
                                    })}
                                    data-testid={`mbooking-nav-${b.id}`}
                                    className="inline-flex items-center gap-1.5 text-xs font-bold bg-sky-50 text-sky-700 dark:bg-sky-950/40 dark:text-sky-400 rounded-full px-3 py-1.5"
                                ><Navigation size={13} /> Navigate</button>
                            )}
                        </div>
                        {/* Money row */}
                        <div className="mt-3 flex items-center justify-between pt-3 border-t border-border text-xs">
                            <span className="text-muted-foreground">Quoted <b className="text-foreground">{inr(b.amount_total)}</b> · Advance {inr(b.advance_paid)}</span>
                            {b.final_amount != null && <span className="font-bold text-blue-700">Final {inr(b.final_amount)}</span>}
                        </div>
                        {/* Actions */}
                        <div className="mt-3 flex gap-2 flex-wrap">
                            {b.status === "PENDING" && (
                                <>
                                    <button onClick={() => setStatus(b, "CONFIRMED")} data-testid={`mbooking-confirm-${b.id}`} className="rounded-xl bg-emerald-600 text-white text-xs font-bold px-3 py-1.5">Confirm</button>
                                    <button onClick={() => setStatus(b, "CANCELLED")} data-testid={`mbooking-cancel-${b.id}`} className="rounded-xl bg-muted text-xs font-bold px-3 py-1.5">Cancel</button>
                                </>
                            )}
                            {b.status !== "COMPLETED" && b.status !== "CANCELLED" && (
                                <button
                                    onClick={() => openCollect(b)}
                                    data-testid={`mbooking-collect-${b.id}`}
                                    className="rounded-xl bg-primary text-primary-foreground text-xs font-bold px-3 py-1.5 inline-flex items-center gap-1.5"
                                ><IndianRupee size={11} /> Collect cash & close</button>
                            )}
                        </div>
                    </div>
                );
            })}

            {collect && (
                <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => !submitting && setCollect(null)}>
                    <div className="bg-card rounded-3xl p-6 w-full max-w-sm shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="mbooking-collect-modal">
                        <h2 className="text-lg font-bold">Collect cash & close</h2>
                        <p className="text-xs text-muted-foreground mt-1">
                            {collect.booking.product_name} · Quoted {inr(collect.booking.amount_total)} · Advance held {inr(collect.booking.advance_paid)}
                        </p>
                        <label className="block mt-4 text-[11px] uppercase tracking-wider font-bold text-muted-foreground">Final amount collected (₹)</label>
                        <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={final}
                            onChange={(e) => setFinal(e.target.value)}
                            data-testid="mbooking-collect-final"
                            className="mt-1 w-full bg-muted rounded-xl px-3 py-2 text-base font-bold outline-none"
                        />
                        <p className="text-[11px] text-muted-foreground mt-1">Defaults to the quoted amount — change if you collected more (extras) or less (discount).</p>
                        <div className="flex gap-2 mt-5">
                            <button onClick={() => setCollect(null)} className="flex-1 rounded-xl bg-muted py-2.5 text-sm font-semibold" data-testid="mbooking-collect-cancel">Cancel</button>
                            <button
                                onClick={submitCollect}
                                disabled={submitting}
                                data-testid="mbooking-collect-confirm"
                                className="flex-1 rounded-xl bg-emerald-600 text-white py-2.5 text-sm font-bold inline-flex items-center justify-center gap-1.5 disabled:opacity-60"
                            >{submitting && <Loader2 size={13} className="animate-spin" />}Confirm</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// Bookings ``address`` can be a structured object OR a free-text
// string (legacy). Render whichever shape is present.
function renderAddress(addr) {
    if (!addr) return null;
    if (typeof addr === "string") return addr;
    const parts = [addr.line1, addr.line2, addr.city, addr.pincode].filter(Boolean);
    return parts.length ? parts.join(", ") : null;
}

